import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import axios from 'axios';
import { Heart, User, MapPin, LogOut, LayoutDashboard, Calendar, Settings, Search, DollarSign, ArrowLeftRight, Phone, UserCircle, Bookmark, Globe, X, ChevronDown, Menu } from 'lucide-react';
import ThemeToggleSwitch from './ThemeToggleSwitch';
import { useAccountMenuDarkMode } from '../../hooks/useAccountMenuDarkMode';
import StoreModal from './StoreModal';
import BrandLogo from './BrandLogo';
import { useLocale } from '../../contexts/LocaleContext';
import { API_URL } from '../../services/api';
import { performClientLogout } from '../../utils/clientLogout';
import '../AllCss/Navbar.css';

/** Survives SPA route changes (each page mounts Navbar); resets on full browser reload. */
let announcementBannerDismissedThisLoad = false;

const MORE_NAV_ITEMS = [
  { key: 'faqSupport', to: '/faq-support' },
  { key: 'whyMyCarSlot', to: '/why-mycarslot' },
  { key: 'searchJobs', to: '/careers' },
];

function Navbar() {
  const navigate = useNavigate();
  const { locale: selectedLocale, localeCountry: selectedLocaleCountry, setLocale } = useLocale();
  const [showMenu, setShowMenu] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { accountMenuDark, toggleAccountMenuDark } = useAccountMenuDarkMode();
  const [showLocaleMenu, setShowLocaleMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [showBanner, setShowBanner] = useState(() => !announcementBannerDismissedThisLoad);

  const dismissAnnouncementBanner = useCallback(() => {
    announcementBannerDismissedThisLoad = true;
    setShowBanner(false);
  }, []);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(() => {
    return localStorage.getItem('selectedZipcode') || '10001';
  });
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const showAccountMenu = isAuthenticated && user && !loggingOut;
  const cars = useSelector((state) => state.cars?.items || []);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isGetFinancedPage = location.pathname === '/get-financed';
  const hasTriedNearestStore = useRef(false);

  // On first visit (no saved zip), try to set store from browser location
  useEffect(() => {
    if (isGetFinancedPage) return;
    if (location.pathname.startsWith('/superadmin')) return;
    if (localStorage.getItem('selectedZipcode')) return;
    if (hasTriedNearestStore.current) return;
    hasTriedNearestStore.current = true;

    const applyDefaultZip = (zip) => {
      setSelectedLocation(zip);
      localStorage.setItem('selectedZipcode', zip);
      window.dispatchEvent(new CustomEvent('zipcodeChanged', { detail: { zipcode: zip } }));
    };

    if (!navigator.geolocation) {
      applyDefaultZip('10001');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            { headers: { 'Accept-Language': 'en', 'User-Agent': 'MyCarSlotStoreLocator/1.0' } }
          );
          const data = await res.json();
          const postcode = data?.address?.postcode;
          const zip = typeof postcode === 'string' && /^\d{5}(-\d{4})?$/.test(postcode.trim())
            ? postcode.trim().substring(0, 5)
            : null;
          if (!zip) {
            applyDefaultZip('10001');
            return;
          }
          const storeRes = await axios.get(`${API_URL}/api/shipping/store-by-zip`, {
        withCredentials: true, params: { zip } });
          const store = storeRes?.data?.data?.store;
          if (store?.zipCode) {
            applyDefaultZip(store.zipCode);
          } else {
            applyDefaultZip(zip);
          }
        } catch {
          applyDefaultZip('10001');
        }
      },
      () => applyDefaultZip('10001'),
      { timeout: 8000, maximumAge: 300000 }
    );
  }, [location.pathname, isGetFinancedPage]);

  // Track scroll for navbar-scrolled class (background stays white)
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const atTop = isHome && !isScrolled;

  // Close user dropdown and locale menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest('.user-profile-section') &&
        !e.target.closest('.user-profile-section-mobile') &&
        !e.target.closest('.mobile-user-dropdown')
      ) {
        setShowMenu(false);
      }
      if (!e.target.closest('.locale-selector')) setShowLocaleMenu(false);
      if (!e.target.closest('.nav-more-menu') && !e.target.closest('.mobile-more-menu')) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLocationChange = (zip) => {
    setSelectedLocation(zip);
    localStorage.setItem('selectedZipcode', zip);
    window.dispatchEvent(new CustomEvent('zipcodeChanged', { detail: { zipcode: zip } }));
  };
  const openStoreModal = () => {
    setShowStoreModal(true);
  };

  // Region/language options
  const localeRegions = [
    {
      name: 'North America',
      countries: [
        { name: 'United States', languages: ['English'], locale: 'en-US' },
        { name: 'Canada', languages: ['English', 'Français'], locale: 'en-CA' },
        { name: 'México', languages: ['Español'], locale: 'es-MX' },
        { name: 'Puerto Rico', languages: ['English', 'Español'], locale: 'en-PR' },
      ],
    },
    {
      name: 'South America',
      countries: [
        { name: 'Chile', languages: ['Español'], locale: 'es-CL' },
        { name: 'Colombia', languages: ['Español'], locale: 'es-CO' },
      ],
    },
    {
      name: 'Europe',
      countries: [
        { name: 'United Kingdom', languages: ['English'], locale: 'en-GB' },
        { name: 'France', languages: ['Français'], locale: 'fr-FR' },
        { name: 'Deutschland', languages: ['Deutsch'], locale: 'de-DE' },
        { name: 'España', languages: ['Español'], locale: 'es-ES' },
        { name: 'Italia', languages: ['Italiano'], locale: 'it-IT' },
        { name: 'Nederland', languages: ['Nederlands'], locale: 'nl-NL' },
        { name: 'Portugal', languages: ['Português'], locale: 'pt-PT' },
        { name: 'Other Europe', languages: ['English'], locale: 'en-EU' },
      ],
    },
    {
      name: 'Asia Pacific',
      countries: [
        { name: 'India', languages: ['English'], locale: 'en-IN' },
        { name: '日本', languages: ['日本語'], locale: 'ja-JP' },
        { name: 'Australia', languages: ['English'], locale: 'en-AU' },
        { name: 'Singapore', languages: ['English'], locale: 'en-SG' },
      ],
    },
  ];

  const handleLocaleSelect = (locale, countryName) => {
    setLocale(locale, countryName);
    setShowLocaleMenu(false);
    window.dispatchEvent(new CustomEvent('localeChanged', { detail: { locale, country: countryName } }));
  };

  // Navbar translations by language (locale prefix: en, es, de, fr, pt, it, nl, ja)
  const navTranslations = useMemo(() => {
    const lang = (selectedLocale || 'en-US').split('-')[0];
    const t = {
      en: { languageName: 'English', inventory: 'Inventory', finance: 'Finance', sellTrade: 'Sell/Trade-in', contact: 'Contact', more: 'More', faqSupport: 'FAQ & support', whyMyCarSlot: 'Why MyCarSlot', searchJobs: 'Search jobs', searchPlaceholder: 'What kind of car are you looking for?', signIn: 'Sign In', saved: 'Saved' },
      es: { languageName: 'Español', inventory: 'Inventario', finance: 'Finanzas', sellTrade: 'Vender/Canje', contact: 'Contacto', more: 'Más', faqSupport: 'FAQ y soporte', whyMyCarSlot: 'Por qué MyCarSlot', searchJobs: 'Buscar empleo', searchPlaceholder: '¿Qué tipo de auto buscas?', signIn: 'Iniciar sesión', saved: 'Guardados' },
      fr: { languageName: 'Français', inventory: 'Inventaire', finance: 'Finance', sellTrade: 'Vente/Reprise', contact: 'Contact', more: 'Plus', faqSupport: 'FAQ et support', whyMyCarSlot: 'Pourquoi MyCarSlot', searchJobs: 'Offres d\'emploi', searchPlaceholder: 'Quel type de véhicule cherchez-vous?', signIn: 'Connexion', saved: 'Enregistrés' },
      de: { languageName: 'Deutsch', inventory: 'Bestand', finance: 'Finanzierung', sellTrade: 'Verkauf/Inzahlungnahme', contact: 'Kontakt', more: 'Mehr', faqSupport: 'FAQ & Support', whyMyCarSlot: 'Warum MyCarSlot', searchJobs: 'Jobs suchen', searchPlaceholder: 'Welches Auto suchen Sie?', signIn: 'Anmelden', saved: 'Gespeichert' },
      pt: { languageName: 'Português', inventory: 'Estoque', finance: 'Financiamento', sellTrade: 'Venda/Troca', contact: 'Contato', more: 'Mais', faqSupport: 'FAQ e suporte', whyMyCarSlot: 'Por que MyCarSlot', searchJobs: 'Buscar vagas', searchPlaceholder: 'Que tipo de carro você procura?', signIn: 'Entrar', saved: 'Salvos' },
      it: { languageName: 'Italiano', inventory: 'Inventario', finance: 'Finanziamento', sellTrade: 'Vendita/Ritiro', contact: 'Contatti', more: 'Altro', faqSupport: 'FAQ e supporto', whyMyCarSlot: 'Perché MyCarSlot', searchJobs: 'Cerca lavoro', searchPlaceholder: 'Che tipo di auto cerchi?', signIn: 'Accedi', saved: 'Salvati' },
      nl: { languageName: 'Nederlands', inventory: 'Voorraad', finance: 'Financiering', sellTrade: 'Verkoop/Inruil', contact: 'Contact', more: 'Meer', faqSupport: 'FAQ & support', whyMyCarSlot: 'Waarom MyCarSlot', searchJobs: 'Vacatures', searchPlaceholder: 'Welke auto zoekt u?', signIn: 'Inloggen', saved: 'Opgeslagen' },
      ja: { languageName: '日本語', inventory: '在庫', finance: 'ファイナンス', sellTrade: '売却/下取り', contact: 'お問い合わせ', more: 'その他', faqSupport: 'FAQとサポート', whyMyCarSlot: 'MyCarSlotとは', searchJobs: '採用情報', searchPlaceholder: 'どんな車をお探しですか？', signIn: 'サインイン', saved: '保存' },
    };
    return t[lang] || t.en;
  }, [selectedLocale]);

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeMenuAndScrollTop = () => {
    setShowMenu(false);
    setShowMoreMenu(false);
    handleNavClick();
  };

  const closeMoreMenu = () => {
    setShowMoreMenu(false);
  };

  const handleMoreNavClick = () => {
    closeMoreMenu();
    handleNavClick();
  };

  const handleLogout = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowMenu(false);
    setLoggingOut(true);
    await performClientLogout();
  };

  const handleMobileSearch = (e) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      navigate(`/inventory?search=${encodeURIComponent(mobileSearchQuery.trim())}`);
      setMobileSearchQuery('');
      setShowSuggestions(false);
    }
  };

  // Generate search suggestions based on available cars
  const getSearchSuggestions = useCallback(() => {
    if (!mobileSearchQuery.trim() || mobileSearchQuery.trim().length < 2) {
      return [];
    }

    const query = mobileSearchQuery.toLowerCase().trim();
    const suggestions = [];
    const seen = new Set();

    // Get only available cars
    const availableCars = cars.filter(c => c.status === 'available');

    // Collect unique makes
    const makes = new Set();
    availableCars.forEach(car => {
      if (car.make && car.make.toLowerCase().startsWith(query)) {
        makes.add(car.make);
      }
    });

    // Collect unique make + model combinations
    const makeModels = new Set();
    availableCars.forEach(car => {
      if (car.make && car.model) {
        const makeLower = car.make.toLowerCase();
        const modelLower = car.model.toLowerCase();
        const fullName = `${car.make} ${car.model}`;
        const fullNameLower = `${makeLower} ${modelLower}`;
        
        if (makeLower.startsWith(query) || modelLower.startsWith(query) || fullNameLower.startsWith(query)) {
          makeModels.add(fullName);
        }
      }
    });

    // Collect unique body types
    const bodyTypes = new Set();
    availableCars.forEach(car => {
      if (car.bodyType && car.bodyType.toLowerCase().startsWith(query)) {
        bodyTypes.add(car.bodyType);
      }
    });

    // Add makes first
    Array.from(makes).sort().forEach(make => {
      if (!seen.has(make)) {
        suggestions.push({ type: 'make', label: make, value: make });
        seen.add(make);
      }
    });

    // Add make + model combinations
    Array.from(makeModels).sort().forEach(fullName => {
      if (!seen.has(fullName)) {
        suggestions.push({ type: 'model', label: fullName, value: fullName });
        seen.add(fullName);
      }
    });

    // Add body types
    Array.from(bodyTypes).sort().forEach(bodyType => {
      if (!seen.has(bodyType)) {
        suggestions.push({ type: 'bodyType', label: bodyType, value: bodyType });
        seen.add(bodyType);
      }
    });

    return suggestions.slice(0, 10); // Limit to 10 suggestions
  }, [mobileSearchQuery, cars]);

  const suggestions = useMemo(() => getSearchSuggestions(), [getSearchSuggestions]);

  const handleSuggestionClick = (suggestion) => {
    setMobileSearchQuery(suggestion.value);
    setShowSuggestions(false);
    navigate(`/inventory?search=${encodeURIComponent(suggestion.value)}`);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMobileSearchQuery(value);
    if (value.trim().length >= 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    setSelectedSuggestionIndex(-1);
  };

  const handleInputFocus = () => {
    if (mobileSearchQuery.trim().length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = (e) => {
    // Delay hiding suggestions to allow click on suggestion
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  const announcementBanner = showBanner ? (
    <div className="announcement-banner" role="region" aria-label="Site announcement">
      <div className="banner-content">
        <p>
          New Features:{' '}
          <Link to="/inventory" className="banner-link">Auction to Own Program</Link>
          {' • '}
          <Link to="/profile?tab=interestedIn" className="banner-link">Vehicle sourcing fee from just under $150</Link>
        </p>
        <button
          type="button"
          className="banner-close"
          onClick={dismissAnnouncementBanner}
          aria-label="Dismiss announcement"
        >
          <X size={18} strokeWidth={2.25} aria-hidden />
        </button>
      </div>
    </div>
  ) : null;

  if (isGetFinancedPage) {
    return (
      <>
        {announcementBanner}
        <header
          className="navbar navbar-advanced navbar-get-financed"
          role="banner"
        >
          <div className="navbar-get-financed-inner">
            <Link to="/" className="navbar-get-financed-logo" onClick={handleNavClick}>
              <BrandLogo className="brand-logo-img" />
            </Link>

            <div
              className="navbar-actions navbar-get-financed-actions"
              role="toolbar"
              aria-label="Store, language, saved cars, and account"
            >
              <div className="location-selector">
                <button
                  type="button"
                  className="location-button location-button-icon navbar-get-financed-loc-wide"
                  onClick={(e) => {
                    e.stopPropagation();
                    openStoreModal();
                  }}
                  aria-label={`Store / zip: ${selectedLocation} – click to change`}
                >
                  <MapPin size={20} strokeWidth={2.5} />
                  <span className="navbar-get-financed-zip">{selectedLocation}</span>
                </button>
              </div>
              <StoreModal
                isOpen={showStoreModal}
                onClose={() => setShowStoreModal(false)}
                currentZip={selectedLocation}
                onLocationChange={handleLocationChange}
              />

              <div className="locale-selector">
                <button
                  type="button"
                  className="locale-button locale-button-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLocaleMenu(!showLocaleMenu);
                  }}
                  aria-label="Region and language"
                  aria-expanded={showLocaleMenu}
                >
                  <Globe size={20} strokeWidth={2.5} />
                </button>
              </div>

              <Link
                to="/wishlist"
                className="wishlist-button wishlist-button-icon"
                aria-label={`${navTranslations.saved} / Wishlist`}
              >
                <Heart size={20} strokeWidth={2} />
              </Link>

              {showAccountMenu ? (
                <div className="user-profile-section">
                  <button
                    type="button"
                    className="user-profile-button user-profile-button-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(!showMenu);
                    }}
                    aria-label="Account menu"
                  >
                    <div className="user-avatar">
                      {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                    </div>
                  </button>

                  {showMenu && (
                    <div className={`user-dropdown${accountMenuDark ? ' user-dropdown--dark' : ''}`}>
                      <div className="dropdown-header">
                        <div className="dropdown-avatar">
                          {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div className="dropdown-user-info">
                          <div className="dropdown-name">{user.fullName || 'User'}</div>
                          <div className="dropdown-email">{user.email}</div>
                          {user.customerNumber && (
                            <div className="dropdown-customer-number">Customer #{user.customerNumber}</div>
                          )}
                        </div>
                      </div>

                      <div className="dropdown-section">
                        <div className="dropdown-item theme-toggle-item">
                          <ThemeToggleSwitch isDark={accountMenuDark} onToggle={toggleAccountMenuDark} />
                        </div>
                        <Link to="/dashboard" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                          <LayoutDashboard size={18} />
                          <span>Dashboard</span>
                        </Link>
                        <Link to="/wishlist" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                          <Heart size={18} />
                          <span>My Wishlist</span>
                        </Link>
                        <Link to="/saved-searches" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                          <Bookmark size={18} />
                          <span>My Saved Searches</span>
                        </Link>
                        <Link to="/appointments" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                          <Calendar size={18} />
                          <span>Appointments</span>
                        </Link>
                        <Link to="/profile" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                          <Settings size={18} />
                          <span>Settings</span>
                        </Link>
                      </div>

                      <div className="dropdown-divider" />

                      <button type="button" className="dropdown-item logout-item" onClick={handleLogout}>
                        <LogOut size={18} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/auth" className="auth-button auth-button-icon" aria-label={navTranslations.signIn} onClick={handleNavClick}>
                  <User size={20} strokeWidth={2.5} />
                </Link>
              )}
            </div>
          </div>

          {showMenu && showAccountMenu && (
            <div className={`mobile-user-dropdown${accountMenuDark ? ' mobile-user-dropdown--dark' : ''}`}>
              <div className="dropdown-header">
                <div className="dropdown-avatar">
                  {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="dropdown-user-info">
                  <div className="dropdown-name">{user.fullName || 'User'}</div>
                  <div className="dropdown-email">{user.email}</div>
                  {user.customerNumber && (
                    <div className="dropdown-customer-number">Customer #{user.customerNumber}</div>
                  )}
                </div>
              </div>

              <div className="dropdown-section">
                <div className="dropdown-item theme-toggle-item">
                  <ThemeToggleSwitch isDark={accountMenuDark} onToggle={toggleAccountMenuDark} />
                </div>
                <Link to="/dashboard" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                  <LayoutDashboard size={18} />
                  <span>Dashboard</span>
                </Link>
                <Link to="/wishlist" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                  <Heart size={18} />
                  <span>My Wishlist</span>
                </Link>
                <Link to="/saved-searches" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                  <Bookmark size={18} />
                  <span>My Saved Searches</span>
                </Link>
                <Link to="/appointments" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                  <Calendar size={18} />
                  <span>Appointments</span>
                </Link>
                <Link to="/profile" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                  <Settings size={18} />
                  <span>Settings</span>
                </Link>
              </div>

              <div className="dropdown-divider" />

              <button type="button" className="dropdown-item logout-item" onClick={handleLogout}>
                <LogOut size={18} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </header>

        {showLocaleMenu && (
          <div className="locale-dropdown-overlay" onClick={() => setShowLocaleMenu(false)} aria-hidden="true">
            <div
              className="locale-dropdown"
              role="dialog"
              aria-label="Select region and language"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="locale-dropdown-inner">
                <div className="locale-current">
                  Current: {selectedLocaleCountry} – {navTranslations.languageName}
                </div>
                {localeRegions.map((region) => (
                  <div key={region.name} className="locale-region">
                    <div className="locale-region-name">{region.name}</div>
                    <ul className="locale-country-list">
                      {region.countries.map((country) => (
                        <li key={country.locale}>
                          <button
                            type="button"
                            className="locale-country-item"
                            onClick={() => handleLocaleSelect(country.locale, country.name)}
                          >
                            <span className="locale-country-name">{country.name}</span>
                            <span className="locale-languages">{country.languages.join(', ')}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {announcementBanner}

      {/* Main Navigation – minimal navbar */}
      <header
        className={`navbar navbar-advanced navbar-static-mobile ${isScrolled ? 'navbar-scrolled' : ''} ${atTop ? 'navbar-at-top' : ''}`}
      >
        <div className="navbar-container navbar-container-advanced">
          {/* Mobile Top Bar */}
          <div className="mobile-top-bar">
            {/* Logo and Company Name */}
            <Link to="/" className="navbar-logo-mobile" onClick={handleNavClick}>
              <BrandLogo className="brand-logo-img" />
            </Link>

            {/* Right Actions: Zipcode, Wishlist, Account */}
            <div className="mobile-actions">
              {/* Location/Zipcode */}
              <div className="location-selector-mobile">
                <button 
                  className="location-button-mobile"
                  onClick={(e) => {
                    e.stopPropagation();
                    openStoreModal();
                  }}
                  aria-label={`Store ZIP ${selectedLocation}, change location`}
                >
                  <MapPin size={18} strokeWidth={2.5} aria-hidden="true" />
                  <span className="location-zip-mobile">{selectedLocation}</span>
                </button>
              </div>

              {/* Region / Language */}
              <div className="locale-selector locale-selector-mobile">
                <button
                  type="button"
                  className="locale-button-mobile"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLocaleMenu(!showLocaleMenu);
                  }}
                  aria-label="Region and language"
                  aria-expanded={showLocaleMenu}
                >
                  <Globe size={20} strokeWidth={2.5} />
                </button>
              </div>

              {/* Wishlist */}
              <Link to="/wishlist" className="wishlist-button-mobile" aria-label="Wishlist" onClick={handleNavClick}>
                <Heart size={20} strokeWidth={2} />
              </Link>
              
              {/* Account */}
              {showAccountMenu ? (
                <div className="user-profile-section-mobile">
                  <button 
                    className="user-profile-button-mobile"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(!showMenu);
                    }}
                    aria-label="User menu"
                  >
                    <div className="user-avatar-mobile">
                      {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                    </div>
                  </button>
                </div>
              ) : (
                <Link to="/auth" className="auth-button-mobile" aria-label="Sign in" onClick={handleNavClick}>
                  <User size={20} strokeWidth={2.5} />
                </Link>
              )}
            </div>
          </div>

          {/* Desktop Logo Section */}
          <Link to="/" className="navbar-logo desktop-only" onClick={handleNavClick}>
            <BrandLogo className="brand-logo-img" />
          </Link>

          {/* Mobile Search Bar */}
          <form className="mobile-search-bar" onSubmit={handleMobileSearch}>
            <div className="mobile-search-input-wrapper" style={{ position: 'relative' }}>
              <Search size={16} className="mobile-search-icon" />
              <input
                type="text"
                className="mobile-search-input"
                placeholder={navTranslations.searchPlaceholder}
                value={mobileSearchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  marginTop: '4px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 9999,
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <div style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 'bold', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                    Search
                  </div>
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={`${suggestion.type}-${suggestion.value}-${index}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedSuggestionIndex === index ? '#f1f5f9' : 'white',
                        borderBottom: index < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none'
                      }}
                      onMouseEnter={() => setSelectedSuggestionIndex(index)}
                    >
                      {suggestion.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>

          {/* Center nav + Right actions */}
          <div className="navbar-center-and-right desktop-only">
            <div className="navbar-center-spacer" aria-hidden="true" />
            <div className="navbar-right-group">
            {/* Center: Navigation Links */}
            <nav className="navbar-nav navbar-nav-center" aria-label="Main">
            <NavLink 
              to="/inventory" 
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <span>{navTranslations.inventory}</span>
            </NavLink>
            <NavLink 
              to="/finance" 
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <span>{navTranslations.finance}</span>
            </NavLink>
            <NavLink 
              to="/sell/trade" 
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <span>{navTranslations.sellTrade}</span>
            </NavLink>
            <NavLink 
              to="/contact" 
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <span>{navTranslations.contact}</span>
            </NavLink>
            <div className="nav-more-menu">
              <button
                type="button"
                className={`nav-link nav-more-trigger${showMoreMenu ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMoreMenu((open) => !open);
                }}
                aria-expanded={showMoreMenu}
                aria-haspopup="menu"
              >
                <span>{navTranslations.more}</span>
                <ChevronDown
                  size={16}
                  strokeWidth={2.25}
                  className={`nav-more-chevron${showMoreMenu ? ' nav-more-chevron--open' : ''}`}
                  aria-hidden
                />
              </button>
              {showMoreMenu && (
                <div className="nav-more-dropdown" role="menu" aria-label={navTranslations.more}>
                  {MORE_NAV_ITEMS.map(({ key, to }) => (
                    <Link
                      key={key}
                      to={to}
                      className="nav-more-item"
                      role="menuitem"
                      onClick={handleMoreNavClick}
                    >
                      {navTranslations[key]}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Desktop Search Bar */}
            <form className="desktop-search-bar" onSubmit={handleMobileSearch}>
            <div className="desktop-search-input-wrapper" style={{ position: 'relative' }}>
              <Search size={18} className="desktop-search-icon" />
              <input
                type="text"
                className="desktop-search-input"
                placeholder={navTranslations.searchPlaceholder}
                value={mobileSearchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  marginTop: '4px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 9999,
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <div style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 'bold', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                    Search
                  </div>
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={`${suggestion.type}-${suggestion.value}-${index}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedSuggestionIndex === index ? '#f1f5f9' : 'white',
                        borderBottom: index < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none'
                      }}
                      onMouseEnter={() => setSelectedSuggestionIndex(index)}
                    >
                      {suggestion.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
            </div>
          <div className="navbar-actions">
            {/* Location – icon only */}
            <div className="location-selector">
              <button 
                className="location-button location-button-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  openStoreModal();
                }}
                aria-label={`Store / zip: ${selectedLocation} – click to change`}
              >
                <MapPin size={20} strokeWidth={2.5} />
              </button>
            </div>
            <StoreModal
              isOpen={showStoreModal}
              onClose={() => setShowStoreModal(false)}
              currentZip={selectedLocation}
              onLocationChange={handleLocationChange}
            />

            {/* Region / Language – globe icon */}
            <div className="locale-selector">
              <button
                type="button"
                className="locale-button locale-button-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLocaleMenu(!showLocaleMenu);
                }}
                aria-label="Region and language"
                aria-expanded={showLocaleMenu}
              >
                <Globe size={20} strokeWidth={2.5} />
              </button>
            </div>

            {/* Wishlist Button */}
            <Link to="/wishlist" className="wishlist-button wishlist-button-icon" aria-label={`${navTranslations.saved} / Wishlist`} onClick={handleNavClick}>
              <Heart size={20} strokeWidth={2} />
            </Link>
            
            {/* User Profile or Auth Button */}
            {showAccountMenu ? (
              <div className="user-profile-section">
                <button 
                  className="user-profile-button user-profile-button-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  aria-label="Account menu"
                >
                  <div className="user-avatar">
                    {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                  </div>
                </button>
                
                {showMenu && (
                  <div className={`user-dropdown${accountMenuDark ? ' user-dropdown--dark' : ''}`}>
                    <div className="dropdown-header">
                      <div className="dropdown-avatar">
                        {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="dropdown-user-info">
                        <div className="dropdown-name">{user.fullName || 'User'}</div>
                        <div className="dropdown-email">{user.email}</div>
                        {user.customerNumber && (
                          <div className="dropdown-customer-number">Customer #{user.customerNumber}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="dropdown-section">
                      <div className="dropdown-item theme-toggle-item">
                        <ThemeToggleSwitch isDark={accountMenuDark} onToggle={toggleAccountMenuDark} />
                      </div>
                      <Link to="/dashboard" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                        <LayoutDashboard size={18} />
                        <span>Dashboard</span>
                      </Link>
                      <Link to="/wishlist" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                        <Heart size={18} />
                        <span>My Wishlist</span>
                      </Link>
                      <Link to="/saved-searches" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                        <Bookmark size={18} />
                        <span>My Saved Searches</span>
                      </Link>
                      <Link to="/appointments" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                        <Calendar size={18} />
                        <span>Appointments</span>
                      </Link>
                      <Link to="/profile" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                        <Settings size={18} />
                        <span>Settings</span>
                      </Link>
                    </div>
                    
                    <div className="dropdown-divider"></div>
                    
                    <button className="dropdown-item logout-item" onClick={handleLogout}>
                      <LogOut size={18} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/auth" className="auth-button auth-button-icon" aria-label={navTranslations.signIn} onClick={handleNavClick}>
                <User size={20} strokeWidth={2.5} />
              </Link>
            )}
          </div>
          </div>
        </div>

        {/* Mobile User Dropdown */}
        {showMenu && showAccountMenu && (
          <div className={`mobile-user-dropdown${accountMenuDark ? ' mobile-user-dropdown--dark' : ''}`}>
            <div className="dropdown-header">
              <div className="dropdown-avatar">
                {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
              </div>
              <div className="dropdown-user-info">
                <div className="dropdown-name">{user.fullName || 'User'}</div>
                <div className="dropdown-email">{user.email}</div>
                {user.customerNumber && (
                  <div className="dropdown-customer-number">Customer #{user.customerNumber}</div>
                )}
              </div>
            </div>
            
            <div className="dropdown-section">
              <div className="dropdown-item theme-toggle-item">
                <ThemeToggleSwitch isDark={accountMenuDark} onToggle={toggleAccountMenuDark} />
              </div>
              <Link to="/dashboard" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                <LayoutDashboard size={18} />
                <span>Dashboard</span>
              </Link>
              <Link to="/wishlist" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                <Heart size={18} />
                <span>My Wishlist</span>
              </Link>
              <Link to="/saved-searches" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                <Bookmark size={18} />
                <span>My Saved Searches</span>
              </Link>
              <Link to="/appointments" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                <Calendar size={18} />
                <span>Appointments</span>
              </Link>
              <Link to="/profile" className="dropdown-item" onClick={closeMenuAndScrollTop}>
                <Settings size={18} />
                <span>Settings</span>
              </Link>
            </div>
            
            <div className="dropdown-divider"></div>
            
            <button className="dropdown-item logout-item" onClick={handleLogout}>
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        )}
      </header>

      {/* Region / Language dropdown (shared for desktop and mobile) */}
      {showLocaleMenu && (
        <div className="locale-dropdown-overlay" onClick={() => setShowLocaleMenu(false)} aria-hidden="true">
          <div className="locale-dropdown" role="dialog" aria-label="Select region and language" onClick={(e) => e.stopPropagation()}>
            <div className="locale-dropdown-inner">
              <div className="locale-current">
                Current: {selectedLocaleCountry} – {navTranslations.languageName}
              </div>
              {localeRegions.map((region) => (
                <div key={region.name} className="locale-region">
                  <div className="locale-region-name">{region.name}</div>
                  <ul className="locale-country-list">
                    {region.countries.map((country) => (
                      <li key={country.locale}>
                        <button
                          type="button"
                          className="locale-country-item"
                          onClick={() => handleLocaleSelect(country.locale, country.name)}
                        >
                          <span className="locale-country-name">{country.name}</span>
                          <span className="locale-languages">{country.languages.join(', ')}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom nav: portal to body so position:fixed stays on screen while scrolling */}
      {!location.pathname.startsWith('/superadmin') &&
        typeof document !== 'undefined' &&
        createPortal(
          <nav className="mobile-bottom-nav" aria-label="Main navigation">
            <NavLink
              to="/inventory"
              className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
              title={navTranslations.inventory}
            >
              <Search size={22} strokeWidth={2} />
              <span>
                <span className="mobile-nav-label-full">{navTranslations.inventory}</span>
                <span className="mobile-nav-label-short">{navTranslations.inventory}</span>
              </span>
            </NavLink>
            <NavLink
              to="/Finance"
              className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
              title={navTranslations.finance}
            >
              <DollarSign size={22} strokeWidth={2} />
              <span>
                <span className="mobile-nav-label-full">{navTranslations.finance}</span>
                <span className="mobile-nav-label-short">{navTranslations.finance}</span>
              </span>
            </NavLink>
            <NavLink
              to="/Sell/Trade"
              className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
              title={navTranslations.sellTrade}
            >
              <ArrowLeftRight size={22} strokeWidth={2} />
              <span>
                <span className="mobile-nav-label-full">{navTranslations.sellTrade}</span>
                <span className="mobile-nav-label-short">Trade-in</span>
              </span>
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
              title={navTranslations.contact}
            >
              <Phone size={22} strokeWidth={2} />
              <span>
                <span className="mobile-nav-label-full">{navTranslations.contact}</span>
                <span className="mobile-nav-label-short">{navTranslations.contact}</span>
              </span>
            </NavLink>
            <button
              type="button"
              className={`mobile-nav-item mobile-nav-item--more${showMoreMenu ? ' active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowMoreMenu((open) => !open);
              }}
              title={navTranslations.more}
              aria-expanded={showMoreMenu}
              aria-haspopup="menu"
            >
              <Menu size={22} strokeWidth={2} />
              <span>
                <span className="mobile-nav-label-full">{navTranslations.more}</span>
                <span className="mobile-nav-label-short">{navTranslations.more}</span>
              </span>
            </button>
            {showMoreMenu && (
              <div className="mobile-more-menu" role="menu" aria-label={navTranslations.more}>
                {MORE_NAV_ITEMS.map(({ key, to }) => (
                  <Link
                    key={key}
                    to={to}
                    className="mobile-more-item"
                    role="menuitem"
                    onClick={handleMoreNavClick}
                  >
                    {navTranslations[key]}
                  </Link>
                ))}
              </div>
            )}
            {showAccountMenu ? (
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
                onClick={handleNavClick}
                title="My Account"
              >
                <UserCircle size={22} strokeWidth={2} />
                <span>
                  <span className="mobile-nav-label-full">My Account</span>
                  <span className="mobile-nav-label-short">Account</span>
                </span>
              </NavLink>
            ) : (
              <NavLink
                to="/auth"
                className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
                onClick={handleNavClick}
                title={navTranslations.signIn}
              >
                <UserCircle size={22} strokeWidth={2} />
                <span>
                  <span className="mobile-nav-label-full">{navTranslations.signIn}</span>
                  <span className="mobile-nav-label-short">{navTranslations.signIn}</span>
                </span>
              </NavLink>
            )}
          </nav>,
          document.body
        )}
    </>
  );
}

export default Navbar;